import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import axios from 'axios';
import { jobsEndpoint } from "../../constants";

const JobApplicationPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    application: '',
    form_answers: '',
  });

  const [cvFiles, setCvFiles] = useState([]);
  const [coverLetterFiles, setCoverLetterFiles] = useState([]);
  const [extraFiles, setExtraFiles] = useState([]);

  useEffect(() => {
    const fetchJob = async () => {
      try {
        const response = await axios.get(`${jobsEndpoint}${id}/details/`, {
          headers: { 'Authorization': `Token ${token}` },
        });
        setJob(response.data);
      } catch (err) {
        console.error(err);
        setError('Failed to load job details.');
      } finally {
        setLoading(false);
      }
    };
    fetchJob();
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e, type) => {
    const files = Array.from(e.target.files);
    if (type === 'cv') setCvFiles(files);
    else if (type === 'coverletter') setCoverLetterFiles(files);
    else if (type === 'extra_form_file') setExtraFiles(files);
  };

  const uploadFiles = async (files, fileType) => {
    if (files.length === 0) return [];

    const data = new FormData();
    files.forEach((file, index) => data.append(`file_${index}`, file));
    data.append('file_type', fileType);

    const response = await fetch(`${jobsEndpoint}upload_files/`, {
      method: 'POST',
      headers: { 'Authorization': `Token ${token}` },
      body: data,
    });

    const responseText = await response.text();

    if (!response.ok) {
      let message = `HTTP ${response.status}: ${response.statusText}`;
      try { message = JSON.parse(responseText)?.error || message; } catch {}
      throw new Error(`Failed to upload ${fileType} files: ${message}`);
    }

    try {
      return JSON.parse(responseText).file_ids || [];
    } catch {
      throw new Error('Invalid JSON response from server');
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      if (cvFiles.length === 0) throw new Error('CV/Resume is required');

      setError('Uploading files...');
      const [cvFileIds, coverLetterIds, extraFileIds] = await Promise.all([
        uploadFiles(cvFiles, 'cv'),
        uploadFiles(coverLetterFiles, 'cover_letter'),
        uploadFiles(extraFiles, 'extra_documents'),
      ]);

      setError('Submitting application...');
      const applicantData = {
        job: id,
        form_answers: formData.form_answers || '',
        cv: cvFileIds,
        cover_letter: coverLetterIds,
        extra_form_file: extraFileIds,
      };

      const response = await fetch(`${jobsEndpoint}${id}/applicant_create/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
        },
        body: JSON.stringify(applicantData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit application');
      }

      setSuccess(true);
      setError(null);

      const appliedJobs = JSON.parse(sessionStorage.getItem('appliedJobs') || '[]');
      if (job && !appliedJobs.some(j => j.id === job.id)) {
        sessionStorage.setItem('appliedJobs', JSON.stringify([
          ...appliedJobs,
          { ...job, applied_date: new Date().toISOString() }
        ]));
      }

    } catch (err) {
      console.error('Application submission error:', err);
      setError(err.message || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  const FileUploadBox = ({ id, label, accept, color, icon, files, fileType, required }) => (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className={`border-2 border-dashed border-gray-${required ? '300' : '200'} rounded-lg p-${required ? '6' : '4'} text-center hover:border-${color}-400 transition-colors`}>
        <input
          type="file"
          id={id}
          multiple
          accept={accept}
          onChange={(e) => handleFileChange(e, fileType)}
          className="hidden"
        />
        <label htmlFor={id} className="cursor-pointer">
          <div className={`text-gray-${required ? '400' : '300'} text-${required ? '4xl' : '3xl'} mb-${required ? '2' : '1'}`}>{icon}</div>
          <p className={`text-gray-${required ? '600 font-medium' : '500 text-sm'}`}>
            {required ? `Click to upload your CV/Resume` : `Click to upload ${label.toLowerCase()}`}
          </p>
          {required && <p className="text-xs text-gray-500 mt-1">Supported formats: PDF, DOC, DOCX (Max 10MB)</p>}
        </label>
      </div>
      {files.length > 0 && (
        <div className={`mt-3 p-3 bg-${color}-50 rounded-lg`}>
          <p className={`text-sm text-${color}-800 font-medium mb-2`}>📎 Selected files:</p>
          <ul className={`text-sm text-${color}-700 space-y-1`}>
            {files.map((file, index) => (
              <li key={index} className="flex items-center gap-2">
                <span>•</span>
                <span>{file.name}</span>
                <span className={`text-xs text-${color}-500`}>({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  if (loading) return <p className="text-gray-500 text-sm p-6">Loading job details...</p>;
  if (!job && error) return <p className="text-red-500 text-sm p-6">{error}</p>;

  if (success) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <div className="text-green-600 text-4xl mb-4">✓</div>
          <h2 className="text-2xl font-bold text-green-800 mb-2">Application Submitted!</h2>
          <p className="text-green-700 mb-4">
            Your application for <strong>{job?.position}</strong> at <strong>{job?.company}</strong> has been successfully submitted.
          </p>
          <p className="text-sm text-green-600 mb-6">
            You will receive a confirmation email shortly. The hiring team will review your application and contact you if you're selected for the next steps.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/candidate/jobs')}
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
            >
              Browse More Jobs
            </button>
            <button
              onClick={() => navigate('/candidate/jobs/appliedjobs')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              View Applied Jobs
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 bg-gray-50 min-h-screen">
    {/* Job Header */}
<div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
  <div className="flex items-center gap-4 mb-4">
    {job?.logo
      ? <img src={job.logo} alt="company logo" className="w-16 h-16 rounded-lg object-cover" />
      : (
        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
          <span className="text-xl font-bold text-white">{job?.company?.[0] || 'C'}</span>
        </div>
      )
    }
    <div>
      <h1 className="text-2xl font-bold text-gray-800">{job?.position}</h1>
      <p className="text-gray-600 text-lg">{job?.company}</p>
      <div className="flex items-center gap-3 mt-2 flex-wrap">
        {job?.location && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">{job.location}</span>}
        {job?.type && <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">{job.type}</span>}
        {job?.level && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">{job.level}</span>}
        {job?.employment_type_library && <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">{job.employment_type_library}</span>}
      </div>
    </div>
  </div>

  {/* Description + details */}
  {job?.body && (
    <div className="border-t pt-4 mt-2 flex flex-col gap-4">
      <div>
        <h2 className="text-base font-semibold text-gray-700 mb-1">Job Description</h2>
        <p className="text-gray-500 text-sm whitespace-pre-line">{job.body}</p>
      </div>

      {job?.requirements && (
        <div>
          <h2 className="text-base font-semibold text-gray-700 mb-1">Requirements</h2>
          <p className="text-gray-500 text-sm whitespace-pre-line">{job.requirements}</p>
        </div>
      )}

      {(job?.bonuses_incentives || job?.other_benefits) && (
        <div>
          <h2 className="text-base font-semibold text-gray-700 mb-1">Compensation & Benefits</h2>
          {job.bonuses_incentives && <p className="text-gray-500 text-sm whitespace-pre-line">{job.bonuses_incentives}</p>}
          {job.other_benefits && <p className="text-gray-500 text-sm whitespace-pre-line mt-1">{job.other_benefits}</p>}
        </div>
      )}

      {job?.offer_price && (
        <div>
          <h2 className="text-base font-semibold text-gray-700 mb-1">Salary</h2>
          <p className="text-gray-500 text-sm">{job.offer_price} / {job.salary_period_library || 'month'}</p>
        </div>
      )}
    </div>
  )}
</div>
      {/* Application Form */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
          <span className="text-2xl">📝</span>
          Apply for this Position
        </h2>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <span className="text-red-500 text-xl">⚠️</span>
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        )}

        <div className="space-y-6">
          {/* Cover Letter Text */}
          <div>
            <label htmlFor="application" className="block text-sm font-medium text-gray-700 mb-2">
              Cover Letter / Application Letter
            </label>
            <textarea
              id="application"
              name="application"
              value={formData.application}
              onChange={handleInputChange}
              rows={6}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              placeholder={`Dear Hiring Manager,\n\nI am writing to express my interest in the ${job?.position} position at ${job?.company}...\n\nBest regards,\n[Your Name]`}
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Tell us why you're the perfect fit for this role (Optional)</span>
              <span>{formData.application.length} characters</span>
            </div>
          </div>

          {/* CV Upload */}
          <FileUploadBox
            id="cv"
            label="Upload CV/Resume"
            accept=".pdf,.doc,.docx"
            color="blue"
            icon="📄"
            files={cvFiles}
            fileType="cv"
            required
          />

          {/* Cover Letter Upload */}
          <FileUploadBox
            id="coverletter"
            label="Additional Cover Letter (Optional)"
            accept=".pdf,.doc,.docx"
            color="green"
            icon="📋"
            files={coverLetterFiles}
            fileType="coverletter"
          />

          {/* Extra Files Upload */}
          <FileUploadBox
            id="extra_files"
            label="Additional Documents (Optional)"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            color="purple"
            icon="📁"
            files={extraFiles}
            fileType="extra_form_file"
          />

          {/* Additional Info */}
          <div>
            <label htmlFor="form_answers" className="block text-sm font-medium text-gray-700 mb-2">
              Additional Information
            </label>
            <textarea
              id="form_answers"
              name="form_answers"
              value={formData.form_answers}
              onChange={handleInputChange}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              placeholder="• Why are you interested in this position?&#10;• What makes you a good fit?&#10;• Salary expectations, availability, etc."
            />
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-4 pt-6 border-t">
            <button
              type="button"
              onClick={() => navigate(-1)}
              disabled={submitting}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 px-6 py-3 bg-[#19BFB7] text-white rounded-lg hover:bg-[#16a39d] transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span>Submitting Application...</span>
                </>
              ) : (
                <>
                  <span>🚀</span>
                  <span>Submit Application</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Tips */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-800 mb-2">💡 Application Tips</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Tailor your cover letter to the specific job and company</li>
          <li>• Ensure your CV is up-to-date and relevant to the position</li>
          <li>• Include keywords from the job description in your application</li>
          <li>• Proofread everything before submitting</li>
        </ul>
      </div>
    </div>
  );
};

export default JobApplicationPage;